<script setup lang="ts">
import { Download, FileText } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { isImageFileAttachment } from '~/lib/messageAttachments';
import type { FileAttachment } from '~/types/models';

withDefaults(defineProps<{
    attachments?: FileAttachment[];
    label?: string;
    loading?: boolean;
    emptyText?: string;
}>(), {
    attachments: () => [],
    label: 'Attached files',
    loading: false,
    emptyText: '',
});

function sizeLabel(bytes?: number) {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
</script>

<template>
    <div v-if="loading" class="grid gap-2 sm:grid-cols-2" aria-label="Loading attachments">
        <Skeleton class="h-14 w-full rounded-xl" />
        <Skeleton class="h-14 w-full rounded-xl" />
    </div>
    <div v-else-if="attachments.length" class="space-y-2">
        <p class="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{{ label }}</p>
        <div class="grid gap-2 sm:grid-cols-2">
            <div v-for="attachment in attachments" :key="attachment.fileId" class="flex min-w-0 items-center gap-2 rounded-xl border border-border/70 bg-background/70 px-2.5 py-2">
                <img
                    v-if="isImageFileAttachment(attachment)"
                    :src="attachment.fileURL"
                    :alt="attachment.fileName"
                    class="size-10 shrink-0 rounded-lg object-cover"
                >
                <FileText v-else class="size-4 shrink-0" />
                <div class="min-w-0 flex-1">
                    <p class="truncate text-xs font-medium">{{ attachment.fileName }}</p>
                    <p v-if="sizeLabel(attachment.sizeBytes)" class="text-[10px] text-muted-foreground">{{ sizeLabel(attachment.sizeBytes) }}</p>
                </div>
                <Button as-child variant="ghost" size="icon" class="size-7 shrink-0">
                    <a :href="attachment.fileURL" target="_blank" rel="noopener noreferrer" :aria-label="`Open ${attachment.fileName}`">
                        <Download class="size-3.5" />
                    </a>
                </Button>
                <slot name="actions" :attachment="attachment" />
            </div>
        </div>
    </div>
    <slot v-else name="empty">
        <p v-if="emptyText" class="text-xs text-muted-foreground">{{ emptyText }}</p>
    </slot>
</template>
